/**
 * "Generate themes" review dialog: lists KS `generate_themes` candidates, kept in
 * the browser per scope. The run lives in the qce slice, so the dialog can close mid-run.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Button, Chip, CircularProgress, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";

import TSPrimaryButton from "../../../../components/common/buttons/TSPrimaryButton";
import TSSecondaryButton from "../../../../components/common/buttons/TSSecondaryButton";
import type { RootState } from "../../../../store/state/rootReducer";
import type { ThemeCandidate } from "../../../../store/state/qce/agentService";
import { runGenerateThemes, useQceDispatch, useThemes } from "../../../../store/state/qce/qceSlice";

import { CT_COLORS, ThemeIcon, pickIconKeyForText } from "./icons";
import {
  clearSuggestions,
  loadSuggestions,
  normalizeThemeName,
  saveSuggestions,
  suggestionsStorageKey,
} from "./themeSuggestionsStore";
import type { StoredSuggestions } from "./themeSuggestionsStore";
import type { ThemeDraft } from "./types";

interface GenerateThemesModalProps {
  orgId: string;
  projectId: string;
  /** Generate from these recordings only (omit = the whole project). */
  storyIds?: string[];
  /** Describes the `storyIds` scope, e.g. "In this recording" / "In 3 selected recordings". */
  scopeLabel?: string;
  /** Open the candidate in ThemeModal to review + edit before saving. */
  onPick: (draft: ThemeDraft) => void;
  /** One-click create the candidate as-is. */
  onQuickAdd: (draft: ThemeDraft) => void;
  onClose: () => void;
}

function candidateToDraft(c: ThemeCandidate, iconKey: string, color: string): ThemeDraft {
  return {
    name: c.name,
    description: c.description,
    keywords: (c.keywords || []).join(", "),
    exemplars: c.exemplars || [],
    skill: c.skill || "",
    icon: iconKey,
    color,
  };
}

/**
 * Content only — no Dialog of its own. Rendered by GenerateThemesFlow inside a
 * single shared Dialog, swapped in-place for the review form (no modal-on-modal).
 */
export default function GenerateThemesListContent({
  orgId,
  projectId,
  storyIds,
  scopeLabel,
  onPick,
  onQuickAdd,
  onClose,
}: GenerateThemesModalProps) {
  const dispatch = useQceDispatch();
  const userId = useSelector((s: RootState) => s.auth?.user_id) as string | undefined;
  const storageKey = suggestionsStorageKey(userId || "anonymous", projectId, storyIds);
  const run = useSelector((s: RootState) => s.qce.generateRuns[storageKey]);
  const isRunning = run?.status === "running";
  const { themes, loading: themesLoading } = useThemes(projectId);

  const [stored, setStored] = useState<StoredSuggestions>(() => loadSuggestions(storageKey));
  const hasAutoRun = useRef(false);

  const savedNames = useMemo(
    () => new Set([...themes.map((t) => t.name), ...stored.saved].map(normalizeThemeName)),
    [themes, stored.saved]
  );
  const dismissed = useMemo(() => new Set(stored.dismissed.map(normalizeThemeName)), [stored.dismissed]);
  const visible = stored.candidates.filter((c) => !dismissed.has(normalizeThemeName(c.name)));
  const isSaved = (c: ThemeCandidate) => savedNames.has(normalizeThemeName(c.name));

  // Skip everything already shown, dismissed or saved in the project.
  const generate = () => {
    if (!orgId) return;
    const skipNames = Array.from(
      new Set([...stored.candidates.map((c) => c.name), ...stored.dismissed, ...themes.map((t) => t.name)])
    );
    void dispatch(runGenerateThemes({ orgId, projectId, storyIds, storageKey, skipNames, scopeLabel }));
  };

  // Nothing stored for this scope → generate once, after the saved themes load (so they're skipped).
  useEffect(() => {
    if (hasAutoRun.current || themesLoading || !orgId) return;
    hasAutoRun.current = true;
    if (!stored.candidates.length && !isRunning) generate();
  }, [themesLoading, orgId]);

  // A run finished (here or after the dialog was closed and reopened): pick up its results.
  useEffect(() => {
    if (run?.status === "done") setStored(loadSuggestions(storageKey));
  }, [run?.status, storageKey]);

  // Once every suggestion is saved or dismissed, stop keeping them.
  useEffect(() => {
    const isAllHandled =
      stored.candidates.length > 0 &&
      stored.candidates.every((c) => isSaved(c) || dismissed.has(normalizeThemeName(c.name)));
    if (isAllHandled) clearSuggestions(storageKey);
  }, [stored, savedNames, dismissed, storageKey]);

  const dismiss = (c: ThemeCandidate) => {
    const next = { ...stored, dismissed: [...stored.dismissed, c.name] };
    setStored(next);
    saveSuggestions(storageKey, next);
  };

  const isEmptyResult = run?.status === "done" && visible.length === 0;

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", height: "85vh", maxHeight: "85vh" }}>
        <Box sx={{ p: "24px 24px 0", flexShrink: 0 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb="4px">
            <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "#212427" }}>Generated themes</Typography>
            <IconButton onClick={onClose} size="small" sx={{ "&:focus": { outline: "none" } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography sx={{ fontSize: "14px", color: "#606971", mb: scopeLabel ? "8px" : "16px" }}>
            Suggested themes from {storyIds?.length ? "these recordings" : "this project's recordings"}. Pick one to
            review and save.
          </Typography>
          {scopeLabel && (
            <Typography
              sx={{
                display: "inline-block",
                fontSize: "13px",
                fontWeight: 600,
                color: "#1A7E70",
                backgroundColor: "#F0FAF7",
                borderRadius: "6px",
                px: "8px",
                py: "2px",
                mb: "16px",
              }}
            >
              {scopeLabel}
            </Typography>
          )}
        </Box>

        <Box sx={{ flex: 1, overflow: "auto", minHeight: 0, p: "0 24px" }}>
          {isRunning && visible.length === 0 && (
            <Box display="flex" flexDirection="column" alignItems="center" py="40px" gap="12px">
              <CircularProgress size={26} sx={{ color: "#1A7E70" }} />
              <Typography sx={{ fontSize: "14px", color: "#606971" }}>Analyzing recordings…</Typography>
            </Box>
          )}

          {run?.status === "error" && (
            <Box py="16px" textAlign="center">
              <Typography sx={{ fontSize: "14px", color: "#B91C1C", mb: "12px" }}>{run.error}</Typography>
              <TSSecondaryButton text="Try again" onClick={generate} />
            </Box>
          )}

          {isEmptyResult && (
            <Typography sx={{ fontSize: "14px", color: "#606971", py: "24px", textAlign: "center" }}>
              No new themes found — try a different set of recordings.
            </Typography>
          )}

          {visible.length > 0 && (
            <Box
              display="grid"
              sx={{
                gap: "16px",
                gridTemplateColumns: "repeat(1, 1fr)",
                p: "2px",
              }}
            >
              {visible.map((c, index) => {
                const saved = isSaved(c);
                const iconKey = pickIconKeyForText(`${c.name} ${c.description} ${(c.keywords || []).join(" ")}`);
                const color = CT_COLORS[index % CT_COLORS.length].icon;
                const draft = candidateToDraft(c, iconKey, color);
                return (
                  <Box
                    key={c.name}
                    onClick={() => !saved && onPick(draft)}
                    role="button"
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      border: "1px solid #C0C5C9",
                      borderRadius: "12px",
                      p: "16px",
                      cursor: saved ? "default" : "pointer",
                      opacity: saved ? 0.7 : 1,
                      backgroundColor: "#fff",
                      "&:hover": saved ? undefined : { outline: "1px solid #C0C5C9" },
                    }}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb="12px">
                      <ThemeIcon iconKey={iconKey} color={color} size={36} />
                      <Box display="flex" alignItems="center" gap="4px" flexShrink={0}>
                        {saved ? (
                          <Chip
                            label="Saved"
                            size="small"
                            sx={{ fontSize: "12px", fontWeight: 700, backgroundColor: "#F0FAF7", color: "#1A7E70" }}
                          />
                        ) : (
                          <>
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                onQuickAdd(draft);
                              }}
                              size="small"
                              variant="text"
                              startIcon={<CheckCircleOutlineIcon fontSize="small" />}
                              sx={{
                                textTransform: "none",
                                fontSize: "13px",
                                fontWeight: 700,
                                borderRadius: "6px",
                                color: "#027A48",
                                "&:hover": { backgroundColor: "#ECFDF3" },
                                "&:focus": { outline: "none" },
                              }}
                            >
                              Add
                            </Button>
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                dismiss(c);
                              }}
                              size="small"
                              variant="text"
                              startIcon={<HighlightOffIcon fontSize="small" />}
                              sx={{
                                textTransform: "none",
                                fontSize: "13px",
                                fontWeight: 700,
                                borderRadius: "6px",
                                color: "#606971",
                                "&:hover": { backgroundColor: "#EDEEEF" },
                                "&:focus": { outline: "none" },
                              }}
                            >
                              Ignore
                            </Button>
                          </>
                        )}
                      </Box>
                    </Box>
                    <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#212427", mb: "6px" }}>
                      {c.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: "#606971",
                        mb: "8px",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {c.description}
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap="6px" mt="auto">
                      {(c.keywords || []).slice(0, 3).map((k, ki) => (
                        <Chip key={ki} label={k} size="small" sx={{ fontSize: "12px", backgroundColor: "#F1F2F3" }} />
                      ))}
                      {c.evidence?.length ? (
                        <Chip
                          label={`${c.evidence.length} examples`}
                          size="small"
                          sx={{ fontSize: "12px", backgroundColor: "#E7F1FF", color: "#1D4ED8" }}
                        />
                      ) : null}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {/* Footer: Generate more next to Close. */}
        <Box sx={{ flexShrink: 0, p: "16px 24px", borderTop: "1px solid #E5E7EB" }}>
          <Box display="flex" alignItems="center" gap="12px">
            <Typography sx={{ flex: 1, fontSize: "12px", color: "#606971" }}>
              {isRunning ? "You can close this — we'll let you know when it's done." : ""}
            </Typography>
            <TSSecondaryButton text="Close" onClick={onClose} />
            <TSPrimaryButton
              text={isRunning ? "Generating…" : "Generate more"}
              onClick={generate}
              loading={isRunning}
              disabled={isRunning || !orgId}
              sx={{ fontSize: "15px" }}
            />
          </Box>
        </Box>
      </Box>
    </>
  );
}
